/**
 * Broker API Service
 * 交易商接口的前端API服务
 */

import { authApi } from './AuthApiService';
import { logger } from '../utils/logger';

const API_BASE_URL = '';

export interface BrokerClient {
  userId: string;
  username: string;
  displayName: string | null;
  email: string | null;
  enabled: boolean;
  hedgeRatio: number;
  hedgeCount: number;
  balance: number; // ✅ 添加余额字段
  createdAt: string;
  commissionRate?: number;
  tierCommissionRate?: number;
  tierId?: string;
  autoCommission?: boolean;
}

export interface ClientOrder {
  id: string;
  userId: string;
  username: string;
  displayName: string | null;
  symbol: string;
  side: string;
  type: string;
  quantity: number;
  price: number;
  closePrice: number | null;
  closeTime: string | null;
  status: string;
  takerFee: number;
  createdAt: string;
  updatedAt: string;
  filledAt: string | null;
  positionId: string | null;
}

export interface HedgeOrderCloseRecord {
  id: string;
  orderId: string;
  quantity: number;
  closePrice: number;
  pnl: number;
  fee: number;
  closedAt: string;
}

export interface HedgeOrder {
  id: string;
  clientOrderId: string | null;
  userId: string;
  username: string;
  displayName: string | null;
  symbol: string;
  side: string;
  quantity: number;
  price: number;
  closePrice: number | null;
  closeTime: string | null;
  status: string;
  makerFee: number;
  createdAt: string;
  updatedAt: string;
  filledAt: string | null;
  filledQty?: number; // ✅ 已成交数量
  closedQty?: number; // ✅ 已平仓数量
  closeRecords?: HedgeOrderCloseRecord[]; // ✅ 平仓记录
}

export interface Deposit {
  id: string;
  userId: string;
  username: string;
  displayName: string | null;
  amount: number;
  fee: number;
  netAmount: number;
  method: string;
  status: string;
  isRead: boolean;
  notes: string | null;
  createdAt: string;
  completedAt: string | null;
  readAt: string | null;
  readBy: string | null;
  
  // ✅ 新增：交易商回馈相关字段
  brokerId?: string | null;
  rebateAmount?: number;
  rebateProcessed?: boolean;
  rebateProcessedAt?: string | null;
  rebateError?: string | null;
}

export interface HedgeRecord {
  id: string;
  brokerId: string;
  clientOrderId: string | null;
  clientPositionId: string | null;
  hedgePositionId: string | null;
  symbol: string;
  side: string;
  quantity: number;
  entryPrice: number;
  currentPrice: number | null;
  closePrice: number | null;
  status: string; // "open" | "closed" | "liquidated"
  pnl: number;
  netProfit: number;
  makerFee: number;
  createdAt: string;
  closedAt: string | null;
}

export class BrokerApiService {
  private baseURL = API_BASE_URL;

  /**
   * 获取授权头
   */
  private getAuthHeader(): HeadersInit {
    const token = authApi.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * 处理API错误
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || error.message || '请求失败');
    }
    return response.json();
  }

  /**
   * 获取客户列表
   */
  async getClients(): Promise<BrokerClient[]> {
    try {
      const response = await fetch(`${this.baseURL}/api/broker/clients`, {
        method: 'GET',
        headers: this.getAuthHeader(),
      });

      const data = await this.handleResponse<{ clients: BrokerClient[] }>(response);
      return data.clients;
    } catch (error) {
      logger.error('获取客户列表失败:', error);
      throw error;
    }
  }

  /**
   * 更新客户配置
   */
  async updateClientConfig(
    userId: string,
    config: { enabled: boolean; hedgeRatio: number }
  ): Promise<void> {
    try {
      const response = await fetch(`${this.baseURL}/api/broker/clients/${userId}/config`, {
        method: 'PUT',
        headers: this.getAuthHeader(),
        body: JSON.stringify(config),
      });

      await this.handleResponse(response);
    } catch (error) {
      logger.error('更新客户配置失败:', error);
      throw error;
    }
  }

  /**
   * 获取客户订单
   */
  async getClientOrders(filters?: {
    userId?: string;
    symbol?: string;
    status?: string;
    limit?: number;
  }): Promise<ClientOrder[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.userId) params.append('userId', filters.userId);
      if (filters?.symbol) params.append('symbol', filters.symbol);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const url = `${this.baseURL}/api/broker/client-orders${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeader(),
      });

      const data = await this.handleResponse<{ orders: ClientOrder[]; total: number }>(response);
      return data.orders;
    } catch (error) {
      logger.error('获取客户订单失败:', error);
      throw error;
    }
  }

  /**
   * 获取对冲订单
   */
  async getHedgeOrders(filters?: {
    userId?: string;
    symbol?: string;
    status?: string;
    limit?: number;
  }): Promise<HedgeOrder[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.userId) params.append('userId', filters.userId);
      if (filters?.symbol) params.append('symbol', filters.symbol);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const url = `${this.baseURL}/api/broker/hedge-orders${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeader(),
      });

      const data = await this.handleResponse<{ orders: HedgeOrder[]; total: number }>(response);
      return data.orders;
    } catch (error) {
      logger.error('获取对冲订单失败:', error);
      throw error;
    }
  }

  /**
   * 获取客户存款记录
   */
  async getDeposits(filters?: {
    userId?: string;
    status?: string;
    unreadOnly?: boolean;
    limit?: number;
  }): Promise<{ deposits: Deposit[]; total: number; unreadCount: number }> {
    try {
      const params = new URLSearchParams();
      if (filters?.userId) params.append('userId', filters.userId);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.unreadOnly) params.append('unreadOnly', 'true');
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const url = `${this.baseURL}/api/broker/deposits${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeader(),
      });

      return await this.handleResponse(response);
    } catch (error) {
      logger.error('获取存款记录失败:', error);
      throw error;
    }
  }

  /**
   * 标记存款为已读
   */
  async markDepositAsRead(depositId: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseURL}/api/broker/deposits/${depositId}/read`, {
        method: 'PUT',
        headers: this.getAuthHeader(),
      });

      await this.handleResponse(response);
    } catch (error) {
      logger.error('标记已读失败:', error);
      throw error;
    }
  }

  /**
   * 批量标记所有存款为已读
   */
  async markAllDepositsAsRead(): Promise<void> {
    try {
      const response = await fetch(`${this.baseURL}/api/broker/deposits/mark-all-read`, {
        method: 'PUT',
        headers: this.getAuthHeader(),
      });

      await this.handleResponse(response);
    } catch (error) {
      logger.error('批量标记失败:', error);
      throw error;
    }
  }

  /**
   * ✅ 获取对冲记录
   */
  async getHedgeRecords(options?: {
    status?: string;
    limit?: number;
  }): Promise<{
    records: HedgeRecord[];
    total: number;
  }> {
    try {
      const params = new URLSearchParams();
      if (options?.status) params.append('status', options.status);
      if (options?.limit) params.append('limit', options.limit.toString());

      const url = `${this.baseURL}/api/broker/hedge-records${params.toString() ? `?${params}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeader(),
      });

      return await this.handleResponse<{
        records: HedgeRecord[];
        total: number;
      }>(response);
    } catch (error) {
      logger.error('获取对冲记录失败:', error);
      throw error;
    }
  }
}

// 导出单例
export const brokerApi = new BrokerApiService();

